if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.currentIndex = 0;
        this.zoom = 1;
        this.panX = 0;
        this.panY = 0;

        this.pointers = new Map();
        this.gesture = null;
        this.suppressClickUntil = 0;

        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleWheel = this.handleWheel.bind(this);
        this.handlePointerDown = this.handlePointerDown.bind(this);
        this.handlePointerMove = this.handlePointerMove.bind(this);
        this.handlePointerUp = this.handlePointerUp.bind(this);
      }

      connectedCallback() {
        super.connectedCallback?.();

        this.container = this.querySelector('[role="document"]');
        this.previousButton = this.querySelector('.jp-modal-prev');
        this.nextButton = this.querySelector('.jp-modal-next');
        this.zoomInButton = this.querySelector('.jp-zoom-in');
        this.zoomOutButton = this.querySelector('.jp-zoom-out');

        [
          this.previousButton,
          this.nextButton,
          this.zoomInButton,
          this.zoomOutButton
        ].forEach((button) => {
          if (!button) return;

          button.addEventListener('pointerup', (event) => {
            if (event.pointerType === 'mouse') {
              event.stopPropagation();
            }
          });
        });

        this.previousButton?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.changeMedia(-1);
        });

        this.nextButton?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.changeMedia(1);
        });

        this.zoomInButton?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.changeZoom(0.5);
        });

        this.zoomOutButton?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.changeZoom(-0.5);
        });

        this.container?.addEventListener('wheel', this.handleWheel, {
          passive: false
        });

        this.container?.addEventListener(
          'pointerdown',
          this.handlePointerDown,
          { passive: false }
        );

        this.container?.addEventListener(
          'pointermove',
          this.handlePointerMove,
          { passive: false }
        );

        this.container?.addEventListener('pointerup', this.handlePointerUp);
        this.container?.addEventListener('pointercancel', this.handlePointerUp);

        this.addEventListener('keydown', this.handleKeyDown);

        if (this.container) {
          this.container.style.touchAction = 'none';
          this.container.style.overscrollBehavior = 'contain';
        }
      }

      disconnectedCallback() {
        this.container?.removeEventListener('wheel', this.handleWheel);
        this.container?.removeEventListener(
          'pointerdown',
          this.handlePointerDown
        );
        this.container?.removeEventListener(
          'pointermove',
          this.handlePointerMove
        );
        this.container?.removeEventListener(
          'pointerup',
          this.handlePointerUp
        );
        this.container?.removeEventListener(
          'pointercancel',
          this.handlePointerUp
        );

        this.removeEventListener('keydown', this.handleKeyDown);

        super.disconnectedCallback?.();
      }

      getMediaItems() {
        if (!this.container) return [];

        return Array.from(
          this.container.querySelectorAll(':scope > [data-media-id]')
        );
      }

      getActiveMedia() {
        const items = this.getMediaItems();

        return (
          items.find((item) => item.classList.contains('active')) ||
          items[this.currentIndex] ||
          null
        );
      }

      getActiveImage() {
        const media = this.getActiveMedia();

        if (!media) return null;
        if (media.matches('img')) return media;

        return media.querySelector(
          'img:not(.deferred-media__poster img)'
        );
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      hide() {
        this.resetZoom();
        this.pointers.clear();
        this.gesture = null;
        super.hide();
      }

      showActiveMedia() {
        const items = this.getMediaItems();
        if (!items.length) return;

        const mediaId = this.openedBy?.getAttribute('data-media-id');

        const index = items.findIndex(
          (item) => item.getAttribute('data-media-id') === mediaId
        );

        this.currentIndex = index >= 0 ? index : 0;
        this.activateMedia();
      }

      activateMedia() {
        const items = this.getMediaItems();
        if (!items.length) return;

        this.currentIndex =
          (this.currentIndex + items.length) % items.length;

        items.forEach((item, index) => {
          item.classList.toggle('active', index === this.currentIndex);
        });

        this.resetZoom();
        this.pointers.clear();
        this.gesture = null;
        this.updateNavigation();
      }

      changeMedia(direction) {
        const items = this.getMediaItems();
        if (items.length < 2) return;

        // Always navigate from the media that is actually active.
        const activeIndex = items.findIndex((item) =>
          item.classList.contains('active')
        );

        const current = activeIndex >= 0
          ? activeIndex
          : this.currentIndex;

        this.currentIndex =
          (current + direction + items.length) % items.length;

        this.activateMedia();
      }

      updateNavigation() {
        const multiple = this.getMediaItems().length > 1;

        if (this.previousButton) this.previousButton.hidden = !multiple;
        if (this.nextButton) this.nextButton.hidden = !multiple;

        const controls = this.querySelector('.jp-modal-zoom');

        if (controls) {
          controls.hidden = !this.getActiveImage();
        }
      }

      changeZoom(amount) {
        if (!this.getActiveImage()) return;

        this.zoom = Math.max(1, Math.min(4, this.zoom + amount));

        if (this.zoom === 1) {
          this.panX = 0;
          this.panY = 0;
        }

        this.applyTransform();
      }

      resetZoom() {
        this.zoom = 1;
        this.panX = 0;
        this.panY = 0;
        this.applyTransform();
      }

      applyTransform() {
        const image = this.getActiveImage();
        if (!image) return;

        image.style.transform =
          `translate3d(${this.panX}px, ${this.panY}px, 0) scale(${this.zoom})`;

        image.style.transformOrigin = 'center center';
        image.style.cursor = this.zoom > 1 ? 'grab' : 'zoom-in';
        image.style.touchAction = 'none';
        image.style.userSelect = 'none';
        image.style.webkitUserSelect = 'none';
        image.style.webkitUserDrag = 'none';
      }

      handleWheel(event) {
        if (!this.getActiveImage()) return;

        event.preventDefault();
        event.stopPropagation();

        this.changeZoom(event.deltaY < 0 ? 0.25 : -0.25);
      }

      handlePointerDown(event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        const media = this.getActiveMedia();
        if (!media || !media.contains(event.target)) return;

        if (
          event.target.closest?.(
            '.jp-modal-prev, .jp-modal-next, .jp-modal-zoom, .product-media-modal__toggle'
          )
        ) {
          return;
        }

        if (event.pointerType !== 'mouse') {
          event.preventDefault();
        }

        this.pointers.set(event.pointerId, {
          x: event.clientX,
          y: event.clientY
        });

        try {
          this.container.setPointerCapture(event.pointerId);
        } catch (_) {}

        if (this.pointers.size === 2) {
          const points = Array.from(this.pointers.values());

          this.gesture = {
            type: 'pinch',
            distance: this.distance(points[0], points[1]),
            zoom: this.zoom,
            panX: this.panX,
            panY: this.panY
          };

          return;
        }

        this.gesture = {
          type: this.zoom > 1 ? 'pan' : 'swipe',
          startX: event.clientX,
          startY: event.clientY,
          panX: this.panX,
          panY: this.panY,
          moved: false
        };
      }

      handlePointerMove(event) {
        if (!this.pointers.has(event.pointerId)) return;

        this.pointers.set(event.pointerId, {
          x: event.clientX,
          y: event.clientY
        });

        if (this.pointers.size >= 2) {
          event.preventDefault();

          const points = Array.from(this.pointers.values());
          const distance = this.distance(points[0], points[1]);

          if (this.gesture?.type !== 'pinch') {
            this.gesture = {
              type: 'pinch',
              distance,
              zoom: this.zoom,
              panX: this.panX,
              panY: this.panY
            };

            return;
          }

          this.zoom = Math.max(
            1,
            Math.min(
              4,
              this.gesture.zoom *
                distance / Math.max(1, this.gesture.distance)
            )
          );

          if (this.zoom === 1) {
            this.panX = 0;
            this.panY = 0;
          }

          this.applyTransform();
          return;
        }

        if (!this.gesture) return;

        const dx = event.clientX - this.gesture.startX;
        const dy = event.clientY - this.gesture.startY;

        if (Math.abs(dx) > 6 || Math.abs(dy) > 6) {
          this.gesture.moved = true;
        }

        if (this.zoom > 1 || this.gesture.type === 'pan') {
          event.preventDefault();

          this.gesture.type = 'pan';
          this.panX = this.gesture.panX + dx;
          this.panY = this.gesture.panY + dy;

          this.applyTransform();
        }
      }

      handlePointerUp(event) {
        if (!this.pointers.has(event.pointerId)) return;

        if (event.pointerType === 'mouse') {
          event.stopPropagation();
        }

        const point = this.pointers.get(event.pointerId);
        this.pointers.delete(event.pointerId);

        if (this.pointers.size > 0) {
          this.gesture = null;
          return;
        }

        if (this.gesture && this.gesture.type !== 'pinch') {
          const dx = point.x - this.gesture.startX;
          const dy = point.y - this.gesture.startY;

          if (this.gesture.moved) {
            this.suppressClickUntil = Date.now() + 400;
          }

          if (
            this.zoom === 1 &&
            this.gesture.type === 'swipe' &&
            Math.abs(dx) > 45 &&
            Math.abs(dx) > Math.abs(dy)
          ) {
            this.changeMedia(dx < 0 ? 1 : -1);
          }
        }

        this.gesture = null;
      }

      distance(a, b) {
        return Math.hypot(b.x - a.x, b.y - a.y);
      }

      handleKeyDown(event) {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.changeMedia(1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.changeMedia(-1);
        } else if (event.key === '+' || event.key === '=') {
          event.preventDefault();
          this.changeZoom(0.5);
        } else if (event.key === '-') {
          event.preventDefault();
          this.changeZoom(-0.5);
        }
      }
    }
  );
}